import type { QueueListEntry } from '../../../../../lib/playback/queueDisplay';
import type { GroupedQueue, QueueRecordingGroup, QueueRenderableItem } from './queueTypes';

type TrackOrderMap = Record<string, string[]>;

const reorderEntriesById = <T extends { id: string }>(items: T[], desiredOrder: string[]): T[] => {
  if (!desiredOrder.length) {
    return items;
  }

  const byId = new Map<string, T>();
  const used = new Set<string>();

  for (const item of items) {
    if (item.id) {
      byId.set(item.id, item);
    }
  }

  const ordered: T[] = [];

  for (const id of desiredOrder) {
    const entry = byId.get(id);
    if (entry) {
      ordered.push(entry);
      used.add(id);
    }
  }

  for (const entry of items) {
    if (!used.has(entry.id)) {
      ordered.push(entry);
    }
  }

  return ordered;
};

const mapGroupTracks = (group: QueueRecordingGroup, trackOrderOverrides?: TrackOrderMap | null): QueueListEntry[] => {
  if (!trackOrderOverrides) {
    return group.tracks;
  }

  const override = trackOrderOverrides[group.id] ?? trackOrderOverrides[group.targetGroupId ?? ''];
  if (!override?.length) {
    return group.tracks;
  }

  return reorderEntriesById(group.tracks, override);
};

export const buildQueueRenderableItems = ({
  grouped,
  groupOrderOverride,
  trackOrderOverrides
}: {
  grouped: GroupedQueue;
  groupOrderOverride?: string[] | null;
  trackOrderOverrides?: TrackOrderMap | null;
}): QueueRenderableItem[] => {
  const effectiveGroupOrder =
    groupOrderOverride && groupOrderOverride.length
      ? groupOrderOverride.filter((groupId) => grouped.recordings.has(groupId))
      : grouped.groupOrder;

  return effectiveGroupOrder
    .map((groupId) => {
      const group = grouped.recordings.get(groupId);
      if (!group) {
        return null;
      }
      const resolvedTracks = mapGroupTracks(group, trackOrderOverrides);
      const renderGroup: QueueRecordingGroup = { ...group, tracks: resolvedTracks };
      return { kind: 'group', group: renderGroup } satisfies QueueRenderableItem;
    })
    .filter(Boolean) as QueueRenderableItem[];
};

export const reorderIds = (list: string[], fromIndex: number, toIndex: number): string[] => {
  if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= list.length || toIndex < 0 || toIndex >= list.length) {
    return list;
  }

  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export const deriveDraggableTrackOrder = (group: QueueRecordingGroup): string[] => {
  return group.tracks.map((track) => track.id);
};

export const mergeTrackOrder = (
  trackOrderOverrides: TrackOrderMap | null,
  groupId: string,
  newOrder: string[]
): TrackOrderMap => {
  const base = trackOrderOverrides ? { ...trackOrderOverrides } : {};
  base[groupId] = newOrder;
  return base;
};
